// Models representing the data structures from the backend

export interface Application {
  id: number;
  application_date: string;
  comment: string | null;
  status?: string | null;  // legacy поле
  file_type?: 'specification' | 'electrical_scheme' | 'technical_doc' | null;
  processing_status?: 'processing_pd_pipeline' | 'selecting_products' | 'reselecting_brands' | 'processing_schemes' | null;
  has_spn_results?: boolean;
  has_pd_result?: boolean;  // Флаг наличия pd_result (вместо полного объекта)
  has_report_content?: boolean;  // Флаг наличия report_content (вместо полного текста)
  is_schemes_processed?: boolean;  // Флаг завершения разметки схем (false если есть схемы требующие разметки)
}

// Типы файлов для загрузки
export type FileUploadType = 'specification' | 'electrical_scheme' | 'technical_doc';

// Конфигурация типов файлов
export interface FileTypeConfig {
  key: FileUploadType;
  title: string;
  description: string;
  acceptedFormats: string;
  icon: 'document' | 'bolt' | 'chart';
}

// WebSocket сообщения
export interface WebSocketMessage {
  event: string;
  application_id?: number;
  file_type?: FileUploadType;
  message?: string;
  error?: string;
  filename?: string;
  redirect_url?: string;
}

export interface ETIMClass {
  etim_class_id: string;
  name: string;
  features: ETIMFeature[];
}

export interface ETIMFeature {
  FEATURE_CODE: string;
  name: string;
  VALUE: string | null; // Разрешаем null
  values: string[];
}

export interface DisplayedProduct {
  code: string;
  vendor_code: string;
  brand: string;  // producer/manufacturer
  description?: string; // Добавлено поле для описания продукта
  price?: number;
  currency?: string;
  notes?: string;
  etim_class_id?: string; // ID ETIM класса для группировки
  features?: ETIMFeature[];
  source_url?: string | null; // Добавлено поле для URL источника продукта
}

export interface Displayed3Category {
  id: number;
  name: string | null;
}

export interface Displayed2Category {
  id: number;
  name: string | null;
  level3_category?: Displayed3Category | null;
}

export interface VendorMatchSpec {
  name: string;
  value?: string | null;
}

export interface VendorMatch {
  vendor: string;
  label: string;
  vendor_code?: string | null;
  name?: string | null;
  description?: string | null;
  type_item_name?: string | null;
  series?: string | null;
  unit_name?: string | null;
  specs: VendorMatchSpec[];
}

export interface CncMatchTraceFilter {
  spec_id?: string | null;
  spec_name: string;
  value_raw?: string | null;
  value_number?: number | null;
}

export interface CncMatchTraceRelaxation {
  spec_id?: string | null;
  spec_name: string;
  from_value_raw?: string | null;
  from_value_number?: number | null;
  to_value_raw?: string | null;
  to_value_number?: number | null;
  reason?: string | null;
}

export interface CncMatchTraceCandidate {
  id: number;
  vendor_code?: string | null;
  name?: string | null;
  selected: boolean;
}

export interface CncMatchTrace {
  status: string;
  status_label: string;
  failure_reason?: string | null;
  failure_reason_label?: string | null;
  match_strategy?: string | null;
  match_strategy_label?: string | null;
  type_item_id?: number | null;
  type_item_name?: string | null;
  type_item_confidence?: number | null;
  type_item_reason?: string | null;
  filters: CncMatchTraceFilter[];
  relaxations: CncMatchTraceRelaxation[];
  dropped_filters: CncMatchTraceFilter[];
  candidates_count: number;
  candidates: CncMatchTraceCandidate[];
  selected_cnc_product_id?: number | null;
  selected_vendor_code?: string | null;
  confidence?: number | null;
  decision_reason?: string | null;
  error?: string | null;
}

export interface CncCandidateSelectionResponse {
  spn: SearchedProductName;
  trace: CncMatchTrace;
}

export interface SearchedProductName {
  id: number;
  application_id: number;
  name: string;
  prompt?: string | null;
  description?: string | null;
  article?: string | null;
  quantity?: number | null;
  uom?: string | null;
  section?: string | null; // Добавлено поле для наименования устройства
  category?: Displayed2Category | null; // Updated to use Displayed2Category
  etim_features: ETIMFeature[];
  selected_product_code?: string | null;
  selected_product_details?: DisplayedProduct | null;
  web_search_results: WebSearchResult[] | null; // Добавлено поле для результатов веб-поиска
  //product_variant_choices: ProductVariantChoice[];
  is_searching?: boolean; // Флаг процесса поиска
  searchError?: string;   // Сообщение об ошибке поиска
  filter_sort_params?: FilterSortParameters; // Добавлено поле
  orderNumber?: number;
  parent_spn_id?: number | null; // Добавлено поле для связи с родительским SPN
  vendor_match?: VendorMatch | null;
}

export enum FilterMode {
  ALL_FILTERS = "ALL_FILTERS",
  NO_FEATURES = "NO_FEATURES",
  NO_CATEGORY_NO_FEATURES = "NO_CATEGORY_NO_FEATURES",
}

export enum SortPriority {
  BY_TEXT_SIMILARITY = "BY_TEXT_SIMILARITY",
  BY_APPLIED_FEATURES = "BY_APPLIED_FEATURES",
}

export interface FilterSortParameters {
  filter_mode: FilterMode;
  sort_priority: SortPriority;
}

export interface SearchedProductNameUpdate {
  name?: string;
  prompt?: string;
}

export interface ApplicationDetails {
  id: number;
  application_date: string;
  comment: string | null;
  status?: string | null; // Добавлено поле статуса
  products: SearchedProductName[];
  report_content?: string | null; // Содержимое отчета для схем/документации
  pd_result?: PipelineResult | null; // Результат обработки проектной документации
  file_type?: 'specification' | 'electrical_scheme' | 'technical_doc' | null; // Тип файла
  has_spn_results?: boolean; // Есть ли результаты подбора SPN
  has_pd_result?: boolean;  // Флаг наличия pd_result (для оптимизации)
  has_report_content?: boolean;  // Флаг наличия report_content (для оптимизации)
  processing_status?: 'processing_pd_pipeline' | 'selecting_products' | 'reselecting_brands' | 'processing_schemes' | null; // Расширенный статус обработки
}
  
export interface FileTypeConfig {
  key: FileUploadType;
  title: string;
  description: string;
  acceptedFormats: string;
  icon: 'document' | 'bolt' | 'chart';
}

// WebSocket message types
export interface WebSocketMessage {
  event: string;
  spn_id?: number;
  application_id?: number;
  file_type?: FileUploadType;
  error?: string;
  processed_spns?: number;
  skipped_spns?: number;
  filename?: string;
  message?: string;
  redirect_url?: string;
}

export interface WebSearchResult {
  name: string;
  article: string;
  is_persist: boolean;
  source_url: string;
  brand?: string | null;
  price?: number | null;
}

export interface PublicProductSearchResponse {
  results?: WebSearchResult[];
  remaining_free_requests?: number | null;
  requires_registration?: boolean;
  requires_login?: boolean;
  error?: string;
  reason?: string;
  message?: string;
  retry_after_seconds?: number;
  provider?: string;
  model?: string;
}

export interface TelegramData {
  id: number;
  first_name: string;
  username?: string;
  photo_url?: string;
}

export interface User {
  id: number;
  tg_id?: number;
  tg_name?: string;
  tg_data?: TelegramData;
  email?: string;
}

// Типы для переподбора брендов
export interface CategoryBrandMapping {
  category3Id: number;
  categoryName: string;
  brandName: string;
}

export interface BrandReselectionRequest {
  category_brand_mappings: CategoryBrandMapping[];
}

export interface BrandReselectionResponse {
  status: string;
  message: string;
  application_id: number;
}

// ============ PD Pipeline Result Types ============

export interface SpecItem {
  name: string;
  quantity: number;
  uom: string;
  spn_id?: number | null;
}

export interface DeviceParts {
  device_name: string;
  specs: SpecItem[];
  qty?: number;
  chunk_gcs_uri?: string | null;
}

export interface DevicePartsList {
  devices: DeviceParts[];
}

export interface SchemePageInfo {
  page: number;
  url: string | null;  // GCS URI like gs://bucket/path.pdf
  device_name?: string | null;
  parts?: DevicePartsList | null;
  qty?: number | null;
  processing_status?: string | null;  // Статус обработки схемы: null, 'processing', 'completed'
}

export interface DeviceInfo {
  device: string;
  qty: number;
}

export interface PipelineResult {
  input_pdf_gcs_uri: string;
  bucket_name: string;
  gcs_output_prefix: string;
  total_pages: number;
  total_devices: number;
  devices_list: DeviceInfo[];
  scheme_pages: SchemePageInfo[];
}

export interface PDResultResponse {
  pd_result: PipelineResult | null;
  report_content: string | null;
  processing_status?: string | null;  // Статус обработки заявки: null, 'processing_schemes', 'selecting_products', 'completed'
}

export interface SignedUrlsRequest {
  gcs_uris: string[];
}

export interface SignedUrlsResponse {
  signed_urls: { [key: string]: string | null };
}

// Пользовательские предпочтения по брендам
export interface UserBrandPreference {
  user_id: number;
  level3_category_id: number;
  brand_name: string;
}
